/** The climate risk register — requirement **EXP-17**, as a table page (ADR-0212, P10).
 *
 * > *"Choose strategy, owner, response actions, completion criteria, evidence and review
 * > triggers."*
 *
 * ## The one thing this screen must never do
 *
 * **Print a number for a score.** `basis.Score` refuses to be multiplied — likelihood 4 times
 * impact 5 times "£2.3m exposure" produces a figure that reads like a financial estimate and is
 * not one (RSK-01) — and the API therefore sends a *sentence*: "Likely likelihood, Major
 * consequence (on Five-point)". There is no numeric score field to render, which is the point:
 * a 20 beside a 15 invites a reader to treat the difference as meaningful when the scale does not
 * support it. The Rating column holds those words and nothing else; there is no money column and no
 * heat map (a five-by-five grid coloured red in the corner **is** the product of two ordinals, drawn
 * instead of printed). The argument is behind "Why there is no number" (`risks.basis`).
 *
 * ## What the table has to say
 *
 * - **Who owns it**, because a risk with no accountable person is a risk nobody looks at.
 * - **Whether a rescoring is owed** (RSK-02): a chip on the row, with the sentence behind its info
 *   control. The score is left as it was and the judgement is not made for you.
 * - **Whether the review is overdue**, derived from the date rather than stored.
 * - **That this is the register as it stands today** (ADR-0048 item 4): a short label in the filter
 *   line, found by `register-as-it-is-now`, naming what is pinned instead. It is a chip and not a
 *   paragraph, and its long form is behind the info control beside it.
 *
 * ## Who sees which control
 *
 * Mirrored from `risks.py`'s role lists, and the asymmetry is deliberate: **recording** a risk is
 * an Approver's or an Admin's act because it is a statement about the company, while **responding**
 * is the work and a Contributor does the work. A Viewer and an Auditor read. A control that
 * predictably 403s is the defect UX-04 names, so a role that may not record is told who does, in
 * one sentence, and is offered no form. A risk's response actions are on its own page.
 */

import { Warning } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import type { Risk } from "../api/queries";
import {
  useAddRiskAction,
  useCloseRisk,
  useRecordRisk,
  useRiskScales,
  useRisks,
} from "../api/queries";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { FieldError } from "../components/FieldError";
import { NotYourRole } from "../components/NotYourRole";
import { useFormChecks } from "../components/useFormChecks";
import { criteriaRefusal, triggerRefusal } from "../lib/riskWording";
import {
  ActionButton,
  Drawer,
  EMPTY_FILTERS,
  EmptyAction,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  InfoPopover,
  RowMenu,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { whoCan } from "../shell/whoCan";
import { useGridSort } from "./gridSort";
import { PersonRef } from "./PersonRef";
import { recordMenuItem } from "./RecordLink";
import { ReopenForm, RescoreForm, RewordForm, ScaleForm } from "./RiskForms";
import { keepSearch } from "./search";
import { CATEGORY, HORIZON, STRATEGY } from "./riskWords";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Risks.module.css";
import { useScreen } from "./useScreen";

/** Who records a risk is `shell/whoCan.ts`'s (`putRiskOnRegister`, held to `POST /risks` by a test): a
 * list, not `role !== "viewer"`, so a sixth role added to the API defaults to *not shown the control*. */
const MAY_RECORD: readonly string[] = whoCan("putRiskOnRegister");
const MAY_RESPOND: readonly string[] = ["admin", "approver", "contributor"];
/** The Admin defines the scale (D-13, ADR-0230); `whoCan.test.ts` holds this to `POST /risks/scales`. */
const MAY_DEFINE: readonly string[] = whoCan("defineRiskScale");

type Drawn =
  | { kind: "add" }
  | { kind: "scale" }
  | { kind: "action"; risk: Risk }
  | { kind: "close"; risk: Risk }
  | { kind: "rescore"; risk: Risk }
  | { kind: "reopen"; risk: Risk }
  | { kind: "reword"; risk: Risk }
  | null;

function openActions(risk: Risk): number {
  return risk.actions.filter((action) => !action.completed).length;
}

/** The first words of a strategy ("Reduce"), for a column; the whole sentence is in the form. */
function strategyName(value: string): string {
  return (STRATEGY[value] ?? value).split(" — ")[0] ?? value;
}

function RisksScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const [drawn, setDrawn] = useState<Drawn>(null);
  const showClosed = (filter.values.state ?? []).includes("closed");
  //: The query itself, not `data = []`. A failed read defaulted to an empty array and rendered as
  //: *"Nothing is on the register yet"* — a statement that somebody assessed this company's
  //: exposure and found none, produced by a dropped connection (EXP-29). See `Loaded`.
  const register = useRisks(showClosed);
  /** The **scales** are a dependent read, and an empty list is a claim: the add form renders *"there
   * is no scoring scale yet"* for it — a statement about this workspace's setup — and a dropped
   * request produced it. So the read travels rather than the array (EXP-29). */
  const scalesRead = useRiskScales();
  const scales = scalesRead.data ?? [];

  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not record": a screen
  //: that flashed the refusal sentence on every load would teach an Approver to ignore it.
  const mayRecord = me ? MAY_RECORD.includes(me.role) : undefined;
  const mayRespond = me ? MAY_RESPOND.includes(me.role) : undefined;
  const mayDefine = me ? MAY_DEFINE.includes(me.role) : undefined;

  // **Counts only from a read that succeeded.** A failed refresh keeps the last register in the
  // cache, and a tile over it would be a claim about the company's exposure made by a dropped
  // request (EXP-29). No tile at all is the honest alternative to a zero.
  const known = register.error || register.isPending ? undefined : register.data;
  const live = known?.filter((risk) => risk.state !== "closed") ?? [];
  const overdue = live.filter((risk) => risk.review_overdue).length;
  const owed = live.filter((risk) => risk.needs_rescoring).length;
  const actionsOpen = live.reduce((sum, risk) => sum + openActions(risk), 0);

  const needle = filter.search.trim().toLowerCase();
  const kinds = filter.values.kind ?? [];
  const horizons = filter.values.horizon ?? [];
  const rows = (register.data ?? []).filter(
    (risk) =>
      (kinds.length === 0 || kinds.includes(risk.category)) &&
      (horizons.length === 0 || horizons.includes(risk.horizon)) &&
      (!needle || `${risk.title} ${risk.description} ${risk.score}`.toLowerCase().includes(needle)),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    title: (r) => r.title,
    kind: (r) => CATEGORY[r.category] ?? r.category,
    horizon: (r) => HORIZON[r.horizon] ?? r.horizon,
    review: (r) => r.next_review_on,
  });

  const columns: GridColumn<Risk>[] = [
    {
      id: "title",
      header: "Risk",
      sortable: true,
      minWidth: "170px",
      wrap: true,
      rowHeader: true,
      cell: (risk) => (
        /* The way to this risk's own page: its facts, its actions and the link on to the notes,
           flags and approvals. */
        <span className={own.reviewCell}>
          <Link
            to="/risks/$riskId"
            params={{ riskId: risk.id }}
            search={keepSearch}
            data-testid="risk-open"
            title={risk.description}
          >
            {risk.title}
          </Link>
          {risk.criteria_is_placeholder || risk.trigger_is_placeholder ? (
            /* D-09: stored text the placeholder rule would refuse today. Flagged for a person to fix,
               never rewritten; the sentence is behind the control. */
            <span data-testid="needs-wording" className={own.reviewChips}>
              <StatusChip tone="warn" size="sm">
                Needs wording
              </StatusChip>
              <InfoPopover infoKey="targets.risks-wording" />
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "kind",
      header: "Kind",
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (risk) => CATEGORY[risk.category] ?? risk.category,
    },
    {
      id: "horizon",
      header: "Horizon",
      sortable: true,
      width: "96px",
      cell: (risk) => HORIZON[risk.horizon] ?? risk.horizon,
    },
    {
      id: "rating",
      header: "Rating",
      width: "156px",
      // "Likely likelihood, Possible consequence" is the rating and was cut to its first two words.
      wrap: true,
      value: (risk) => risk.score,
      /* **The score, as the words the server sent.** Never a product, and never a coloured grid
         cell — a five-by-five heat map is the product of two ordinals drawn instead of printed
         (RSK-01). */
      cell: (risk) => (
        <span data-testid="risk-score">{risk.score}</span>
      ),
    },
    {
      id: "response",
      header: "Response",
      width: "88px",
      cell: (risk) => strategyName(risk.strategy),
    },
    {
      id: "owner",
      header: "Owner",
      width: "120px",
      cell: (risk) => <PersonRef userId={risk.owner_user_id} showName />,
    },
    {
      id: "review",
      header: "Next review",
      sortable: true,
      width: "188px",
      cell: (risk) => (
        /* The date on its own line and the chips under it: beside it they made the cell 315px wide in a
           188px column, so "Needs rescoring" and its info control ran under the next column and
           "Add action" covered the control (found by the gate's keyboard check, 2026-11-03). */
        <span className={own.reviewCell}>
          <span>{shortDate(risk.next_review_on)}</span>
          {risk.review_overdue || risk.needs_rescoring ? (
            <span className={own.reviewChips}>
              {risk.review_overdue ? (
                <StatusChip tone="warn" size="sm">
                  Overdue
                </StatusChip>
              ) : null}
              {risk.needs_rescoring ? (
                /* RSK-02, on the row it is about: the score is unchanged and a judgement is owed.
                   The sentence saying the product has not made it is behind the control. */
                <span data-testid="needs-rescoring" className={own.reviewChips}>
                  <StatusChip tone="info" size="sm">
                    Needs rescoring
                  </StatusChip>
                  <InfoPopover infoKey="targets.risks-rescoring" />
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Open actions",
      width: "100px",
      numeric: true,
      cell: (risk) => String(openActions(risk)),
    },
    {
      // One main action and a menu, on one 40px line: three buttons side by side made this column 172px and
      // the table wider than its card (U6, 2026-11-03).
      id: "act",
      header: "Actions",
      headerHidden: true,
      width: "120px",
      hideable: false,
      cell: (risk) => {
        const closed = risk.state === "closed";
        return (
          <RowMenu
            name={risk.title}
            lead={
              closed ? (
                <StatusChip tone="neutral" size="sm">
                  Closed
                </StatusChip>
              ) : undefined
            }
            main={
              !closed && mayRespond !== false
                ? {
                    label: "Add action",
                    testId: "add-action",
                    ariaLabel: `Add a response action to: ${risk.title}`,
                    onSelect: () => setDrawn({ kind: "action", risk }),
                  }
                : undefined
            }
            items={[
              recordMenuItem({ recordId: risk.id, kind: "climate_risk", subject: risk.title }),
              ...(!closed && mayRecord !== false
                ? [
                    { label: "Assess again", testId: "rescore-risk", onSelect: () => setDrawn({ kind: "rescore", risk }) },
                    { label: "Reword", testId: "reword-risk", onSelect: () => setDrawn({ kind: "reword", risk }) },
                    { label: "Close this risk", testId: "close-risk", onSelect: () => setDrawn({ kind: "close", risk }) },
                  ]
                : []),
              ...(closed && mayRecord !== false
                ? [{ label: "Reopen this risk", testId: "reopen-risk", onSelect: () => setDrawn({ kind: "reopen", risk }) }]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const kindOptions = Object.entries(CATEGORY).map(([value, label]) => ({ value, label }));
  const horizonOptions = Object.entries(HORIZON).map(([value, label]) => ({ value, label }));

  return (
    <ScreenFrame screen={screen} title="Climate risks" crumb="what we are exposed to, and what we intend to do" bodyScrolls>
      <TablePage
        icon={Warning}
        title="Targets & plan"
        label="Climate risks"
        actions={
          mayRecord === true || mayDefine === true ? (
            <>
              {/* The Admin's: the scale every score is read against. Offered to no other role (D-13). */}
              {mayDefine === true ? (
                <ActionButton data-action="scale" onClick={() => setDrawn({ kind: "scale" })}>
                  Scoring scale
                </ActionButton>
              ) : null}
              {mayRecord === true ? (
                <ActionButton variant="primary" data-action="add" onClick={() => setDrawn({ kind: "add" })}>
                  Add risk
                </ActionButton>
              ) : null}
            </>
          ) : undefined
        }
        tiles={
          known && known.length > 0 ? (
            /* Four counts, each a thing to do or a thing to know: not a score, and nothing here is a
               money figure. */
            <TileStrip label="Where the register needs attention">
              <Tile value={String(live.length)} label="On the register" testId="risk-live" />
              <Tile value={String(overdue)} label="Review overdue" testId="risk-overdue" />
              <Tile value={String(owed)} label="Awaiting a rescoring" testId="risk-rescoring" />
              <Tile value={String(actionsOpen)} label="Open response actions" testId="risk-open-actions" />
            </TileStrip>
          ) : undefined
        }
        filters={
          known && known.length === 0 ? undefined : (
          <FilterLine
            filters={[
              { id: "kind", label: "Kind", options: kindOptions },
              { id: "horizon", label: "Horizon", options: horizonOptions },
              {
                id: "state",
                label: "Closed",
                mode: "single",
                allLabel: "Hidden",
                options: [{ value: "closed", label: "Shown" }],
              },
            ]}
            state={filter}
            onChange={setFilter}
            resultCount={
              known ? { shown: rows.length, total: (register.data ?? []).length, noun: "risks" } : undefined
            }
            actions={
              <>
                {/* **"As it is now", said on the screen — ADR-0048 item 4.** What was stated is
                    pinned by the filing's own issued artifact, so the live register stays
                    as-it-is-now and must be labelled that way. A label, not a paragraph; the long
                    form is one control away. Not gated on rows: an empty register is today's empty
                    register. */}
                <StatusChip tone="neutral" size="sm" testId="register-as-it-is-now">
                  As it stands today; a filing pins what was stated
                </StatusChip>
                <InfoPopover infoKey="targets.risks-as-now" />
                <HowDrawer infoKey="targets.risks-basis" label="Why there is no number" />
              </>
            }
          />
          )
        }
        table={
          register.error ? undefined : (
            <>
              {/* The scoring scales are read for the Add form, and a read that failed is said on the page
                  where the reader can retry it - not only in a drawer that is closed (EXP-29). Only a role
                  that may record needs them. */}
              {mayRecord === true && scalesRead.error ? (
                <ReadFailed
                  of="The scoring scales"
                  error={scalesRead.error}
                  onRetry={() => void scalesRead.refetch()}
                />
              ) : null}
              {mayRecord === false ? <NotYourRole action="putRiskOnRegister" testId="not-permitted" /> : null}
              <GridTable
                caption="The climate risk register"
                columns={columns}
                rows={sorted}
                rowKey={(risk) => risk.id}
                rowAttributes={(risk) => ({ "data-testid": "risk", "data-state": risk.state })}
                loading={register.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    compact
                    testId="risks-empty"
                    icon={(register.data ?? []).length > 0 ? undefined : Warning}
                    title={
                      (register.data ?? []).length > 0
                        ? "No risk matches the filters."
                        : "No risks yet"
                    }
                    action={
                      (register.data ?? []).length > 0 ? undefined : (
                        // The sentence naming whose act it is stands above the table for a role that
                        // may not record, so none is repeated here.
                        <EmptyAction
                          label="Add risk"
                          allowed={mayRecord}
                          onClick={() => setDrawn({ kind: "add" })}
                          testId="risks-empty-add"
                        />
                      )
                    }
                  >
                    {(register.data ?? []).length > 0
                      ? null
                      : mayRecord === false
                        ? "Nothing has been put on the register yet."
                        : "Put a climate risk on the register, and respond to it."}
                  </EmptyState>
                }
                testId="risks"
              />
            </>
          )
        }
        state={
          register.error ? (
            <ReadFailed
              of="The risk register"
              error={register.error}
              onRetry={() => void register.refetch()}
            />
          ) : undefined
        }
      />

      <Drawer
        open={drawn?.kind === "add"}
        onClose={() => setDrawn(null)}
        title="Add a risk"
        width="md"
        testId="risk-add-drawer"
      >
        <AddRisk
          scales={scales}
          scalesFailed={scalesRead.error}
          onRetryScales={() => void scalesRead.refetch()}
          ownerId={me?.user_id}
          mayDefine={mayDefine === true}
          onDefine={() => setDrawn({ kind: "scale" })}
        />
      </Drawer>

      <Drawer
        open={drawn?.kind === "scale"}
        onClose={() => setDrawn(null)}
        title="Scoring scale"
        width="md"
        testId="risk-scale-drawer"
      >
        {drawn?.kind === "scale" ? <ScaleForm scales={scales} onDone={() => setDrawn(null)} /> : null}
      </Drawer>

      <Drawer
        open={drawn?.kind === "rescore"}
        onClose={() => setDrawn(null)}
        title="Assess this risk again"
        width="md"
        testId="risk-rescore-drawer"
      >
        {drawn?.kind === "rescore" ? (
          <RescoreForm risk={drawn.risk} scales={scales} onDone={() => setDrawn(null)} />
        ) : null}
      </Drawer>

      <Drawer
        open={drawn?.kind === "reopen"}
        onClose={() => setDrawn(null)}
        title="Reopen this risk"
        width="sm"
        testId="risk-reopen-drawer"
      >
        {drawn?.kind === "reopen" ? <ReopenForm risk={drawn.risk} onDone={() => setDrawn(null)} /> : null}
      </Drawer>

      <Drawer
        open={drawn?.kind === "reword"}
        onClose={() => setDrawn(null)}
        title="Reword this risk"
        width="md"
        testId="risk-reword-drawer"
      >
        {drawn?.kind === "reword" ? <RewordForm risk={drawn.risk} onDone={() => setDrawn(null)} /> : null}
      </Drawer>

      <Drawer
        open={drawn?.kind === "action"}
        onClose={() => setDrawn(null)}
        title="Add a response action"
        width="sm"
        testId="risk-action-drawer"
      >
        {drawn?.kind === "action" ? (
          <ActionForm risk={drawn.risk} onDone={() => setDrawn(null)} reload={() => void register.refetch()} />
        ) : null}
      </Drawer>

      <Drawer
        open={drawn?.kind === "close"}
        onClose={() => setDrawn(null)}
        title="Close this risk"
        width="sm"
        testId="risk-close-drawer"
      >
        {drawn?.kind === "close" ? (
          <CloseForm risk={drawn.risk} onDone={() => setDrawn(null)} reload={() => void register.refetch()} />
        ) : null}
      </Drawer>
    </ScreenFrame>
  );
}

/** A response action on one risk. "Not now" **is** the cancel, and it restores the prior state: the
 * drawer is unmounted with the half-typed action, so it cannot reappear as work somebody appears to
 * have planned. */
function ActionForm({
  risk,
  onDone,
  reload,
}: {
  risk: Risk;
  onDone: () => void;
  reload: () => void;
}) {
  const [description, setDescription] = useState("");
  const [due, setDue] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const addAction = useAddRiskAction(risk.id);

  async function submit() {
    setRefusal(null);
    try {
      await addAction.mutateAsync({
        description,
        owner_user_id: risk.owner_user_id,
        due_on: due,
      });
      onDone();
    } catch (error) {
      setRefusal(error);
    }
  }

  return (
    <div className={own.form}>
      <label className={styles.field}>
        <span>What will be done?</span>
        <input
          aria-label="What will be done?"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Commission a flood survey of the site."
        />
      </label>
      <label className={styles.field}>
        <span>By when?</span>
        <input aria-label="By when?" type="date" value={due} onChange={(event) => setDue(event.target.value)} />
      </label>
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={!description.trim() || !due || addAction.isPending}
          onClick={() => void submit()}
          data-testid="save-action"
        >
          {addAction.isPending ? "Adding…" : "Add it"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <RiskRefusal refusal={refusal} reload={reload} onDone={onDone} clear={() => setRefusal(null)} />
    </div>
  );
}

function CloseForm({
  risk,
  onDone,
  reload,
}: {
  risk: Risk;
  onDone: () => void;
  reload: () => void;
}) {
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const closeRisk = useCloseRisk(risk.id);
  const open = openActions(risk);

  async function submit() {
    setRefusal(null);
    try {
      await closeRisk.mutateAsync(reason);
      onDone();
    } catch (error) {
      setRefusal(error);
    }
  }

  return (
    <div className={own.form}>
      <label className={styles.field}>
        <span>Why is this closed?</span>
        <textarea
          aria-label="Why is this closed?"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Flood defences installed and signed off by the insurer."
        />
      </label>
      {open > 0 ? (
        <p className={styles.note}>
          {open} response action{open === 1 ? "" : "s"} {open === 1 ? "is" : "are"} still open.
          EmitIQ will refuse this: a risk closed over outstanding work is the one claim a register
          must not make.
        </p>
      ) : null}
      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={!reason.trim() || closeRisk.isPending}
          onClick={() => void submit()}
          data-testid="save-close"
        >
          {closeRisk.isPending ? "Closing…" : "Close it"}
        </button>
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <RiskRefusal refusal={refusal} reload={reload} onDone={onDone} clear={() => setRefusal(null)} />
    </div>
  );
}

/** **A risk closed over open work is refused with the count, and that is a 422** — not a permission
 * and not a conflict, so it keeps the server's sentence and gets a retry only once something has
 * changed. The stale and unknown branches offer a reload instead. */
function RiskRefusal({
  refusal,
  reload,
  onDone,
  clear,
}: {
  refusal: unknown;
  reload: () => void;
  onDone: () => void;
  clear: () => void;
}) {
  return (
    <WriteFailed
      error={refusal}
      of="this risk"
      testId="risk-refused"
      onReload={() => {
        reload();
        clear();
        onDone();
      }}
    />
  );
}

/** Recording a risk: six choices, each asked for in the company's words.
 *
 * The scale select offers only the scales that have **not** been replaced — scoring a new risk on
 * a scale the company has moved off produces a register where two rows' fours mean different
 * things, which is what replacing a scale exists to prevent. The API refuses it too; this is so
 * nobody is offered it.
 */
function AddRisk({
  scales,
  scalesFailed,
  onRetryScales,
  ownerId,
  mayDefine,
  onDefine,
}: {
  scales: ReadonlyArray<{ id: string; name: string; labels: string[]; superseded: boolean }>;
  /** Whether the scale read **failed**, which is not the same as there being no scale. */
  scalesFailed: unknown;
  onRetryScales: () => void;
  ownerId: string | undefined;
  /** The Admin defines the scale (D-13): only they are offered the way to. */
  mayDefine: boolean;
  onDefine: () => void;
}) {
  const live = scales.filter((scale) => !scale.superseded);
  const record = useRecordRisk();
  const [scaleId, setScaleId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("physical_acute");
  const [horizon, setHorizon] = useState("medium");
  const [strategy, setStrategy] = useState("reduce");
  const [likelihood, setLikelihood] = useState("");
  const [consequence, setConsequence] = useState("");
  const [criteria, setCriteria] = useState("");
  const [trigger, setTrigger] = useState("");
  const [review, setReview] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [checks, attach] = useFormChecks<HTMLDivElement>();

  /** Put the form back as it was: every field, including the four the submit path leaves. A
   * cancelled risk that keeps its likelihood and its review date hands the next one somebody
   * records a score they did not choose. */
  function clearForm() {
    setScaleId("");
    setTitle("");
    setDescription("");
    setCategory("physical_acute");
    setHorizon("medium");
    setStrategy("reduce");
    setLikelihood("");
    setConsequence("");
    setCriteria("");
    setTrigger("");
    setReview("");
    setRefusal(null);
    setDone(false);
    checks.clear();
  }

  const scale = live.find((candidate) => candidate.id === (scaleId || live[0]?.id));

  async function submit() {
    setRefusal(null);
    setDone(false);
    // **The button is never greyed for these** (Q-06): pressing it says which box is wrong, beside the
    // box. The two texts are held to the same placeholder rule the server applies (D-09, G-P8-33).
    const ok = checks.run([
      { field: "title", ok: title.trim() !== "", message: "Name the risk, such as Flood risk at the Trafford site." },
      { field: "description", ok: description.trim() !== "", message: "Describe the risk in a sentence." },
      { field: "likelihood", ok: likelihood !== "", message: "Choose how likely it is." },
      { field: "consequence", ok: consequence !== "", message: "Choose how bad it would be." },
      {
        field: "completion_criteria",
        ok: criteriaRefusal(criteria) === null,
        message: criteriaRefusal(criteria) ?? "",
      },
      {
        field: "review_trigger",
        ok: triggerRefusal(trigger) === null,
        message: triggerRefusal(trigger) ?? "",
      },
      { field: "next_review_on", ok: review !== "", message: "Choose the date of the next scheduled review." },
    ]);
    if (!ok || !scale || !ownerId) return;
    try {
      await record.mutateAsync({
        title,
        description,
        category,
        horizon,
        scale_id: scale.id,
        likelihood: Number(likelihood),
        consequence: Number(consequence),
        strategy,
        owner_user_id: ownerId,
        completion_criteria: criteria,
        review_trigger: trigger,
        next_review_on: review,
      });
      setDone(true);
      setTitle("");
      setDescription("");
      setCriteria("");
      setTrigger("");
    } catch (error) {
      // A field the server named is said beside that field; anything else is the general failure.
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  // **Before the "there is no scale" branch.** That sentence tells somebody to go and define a
  // scale, which is the wrong instruction when the request for the list failed — and it is the
  // instruction they cannot act on, because defining one is not built here either.
  if (scalesFailed) {
    return <ReadFailed of="The scoring scales" error={scalesFailed} onRetry={onRetryScales} />;
  }

  if (live.length === 0) {
    return (
      <>
        <p className={styles.note} data-testid="no-scale">
          <strong>There is no scoring scale yet.</strong> A likelihood of 4 means nothing without the
          scale it was read against — two companies&rsquo; fours are different judgements, and so are
          one company&rsquo;s across two years. The scale has to be defined before a risk is recorded.
        </p>
        {mayDefine ? (
          <p>
            <button type="button" className={styles.primary} data-testid="define-scale" onClick={onDefine}>
              Define the scale
            </button>
          </p>
        ) : (
          /* Not a dead end: the sentence says who can (design rule 6). */
          <NotYourRole action="defineRiskScale" testId="scale-not-yours" />
        )}
      </>
    );
  }

  return (
    <div className={own.form} ref={attach}>
      <label className={styles.field}>
        <span>What is the risk?</span>
        <input
          aria-label="What is the risk?"
          {...checks.field("title")}
          value={title}
          onChange={(event) => {
            checks.touch("title");
            setTitle(event.target.value);
          }}
          placeholder="Flood risk at the Trafford site"
        />
        <FieldError {...checks.say("title")} />
      </label>
      <label className={styles.field}>
        <span>Describe it</span>
        <textarea
          aria-label="Describe it"
          rows={2}
          {...checks.field("description")}
          value={description}
          onChange={(event) => {
            checks.touch("description");
            setDescription(event.target.value);
          }}
          placeholder="The site sits within a Zone 3 flood area."
        />
        <FieldError {...checks.say("description")} />
      </label>
      <label className={styles.field}>
        <span>What kind?</span>
        <select
          aria-label="What kind?"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          {Object.entries(CATEGORY).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.field}>
        <span>When would it bite?</span>
        <select
          aria-label="When would it bite?"
          value={horizon}
          onChange={(event) => setHorizon(event.target.value)}
        >
          {Object.entries(HORIZON).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      {/* One scale is a statement, several are a choice — the same rule as the period selector in
          the top bar: a select with a single option looks like a control that does nothing. A
          replaced scale is not offered at all, because scoring a new risk on one produces a
          register where two rows' fours mean different things. */}
      {live.length > 1 ? (
        <label className={styles.field}>
          <span>Scored against</span>
          <select
            aria-label="Scored against"
            value={scale?.id ?? ""}
            onChange={(event) => {
              setScaleId(event.target.value);
              // The positions are chosen by **label**, and the labels belong to a scale — so a
              // half-made choice on the old one is cleared rather than carried across, where
              // "Likely" on five points would silently become position 4 on three.
              setLikelihood("");
              setConsequence("");
            }}
          >
            {live.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* **Positions chosen by their words**, not by typing a number. The labels are what the
          company agreed each position means; a bare 1–5 spinner is how two people choose the same
          number and mean different things by it. */}
      <label className={styles.field}>
        <span>How likely?</span>
        <select
          aria-label="How likely?"
          {...checks.field("likelihood")}
          value={likelihood}
          onChange={(event) => {
            checks.touch("likelihood");
            setLikelihood(event.target.value);
          }}
        >
          <option value="">Choose</option>
          {scale?.labels.map((label, index) => (
            <option key={label} value={index + 1}>
              {label}
            </option>
          ))}
        </select>
        <FieldError {...checks.say("likelihood")} />
      </label>
      <label className={styles.field}>
        <span>How bad, if it happened?</span>
        <select
          aria-label="How bad, if it happened?"
          {...checks.field("consequence")}
          value={consequence}
          onChange={(event) => {
            checks.touch("consequence");
            setConsequence(event.target.value);
          }}
        >
          <option value="">Choose</option>
          {scale?.labels.map((label, index) => (
            <option key={label} value={index + 1}>
              {label}
            </option>
          ))}
        </select>
        <FieldError {...checks.say("consequence")} />
      </label>

      <label className={styles.field}>
        <span>What will you do about it?</span>
        <select
          aria-label="What will you do about it?"
          value={strategy}
          onChange={(event) => setStrategy(event.target.value)}
        >
          {Object.entries(STRATEGY).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.field}>
        <span>Complete when?</span>
        <textarea
          aria-label="Complete when?"
          rows={2}
          {...checks.field("completion_criteria")}
          value={criteria}
          onChange={(event) => {
            checks.touch("completion_criteria");
            setCriteria(event.target.value);
          }}
          placeholder="Flood defences installed and signed off by the insurer."
        />
        <FieldError {...checks.say("completion_criteria")} />
      </label>
      <label className={styles.field}>
        <span>Review it if what happens?</span>
        <textarea
          aria-label="Review it if what happens?"
          rows={2}
          {...checks.field("review_trigger")}
          value={trigger}
          onChange={(event) => {
            checks.touch("review_trigger");
            setTrigger(event.target.value);
          }}
          placeholder="A new Environment Agency flood map for the area."
        />
        <FieldError {...checks.say("review_trigger")} />
      </label>
      <p className={styles.note}>
        A review date with nothing that would bring it forward is a diary entry. Name the thing that
        would make this assessment wrong.
      </p>
      <label className={styles.field}>
        <span>Next scheduled review</span>
        <input
          aria-label="Next scheduled review"
          type="date"
          {...checks.field("next_review_on")}
          value={review}
          onChange={(event) => {
            checks.touch("next_review_on");
            setReview(event.target.value);
          }}
        />
        <FieldError {...checks.say("next_review_on")} />
      </label>

      <p className={own.rowActions}>
        <button
          type="button"
          className={styles.primary}
          disabled={record.isPending}
          onClick={() => void submit()}
          data-testid="record-risk"
        >
          {record.isPending ? "Recording…" : "Put it on the register"}
        </button>
        {/* Cancel appears only once there is something to cancel, and it restores the whole form
            rather than one field. */}
        {title || description || likelihood || consequence || criteria || trigger ? (
          <button
            type="button"
            className={styles.fixLink}
            data-testid="cancel-risk"
            onClick={clearForm}
          >
            Cancel
          </button>
        ) : null}
      </p>

      {done ? (
        <p className={styles.note} data-testid="risk-recorded">
          <strong>On the register.</strong> You own it until somebody else does — add the response
          actions beside it.
        </p>
      ) : null}
      <WriteFailed error={refusal} of="the risk register" testId="add-refused" />
    </div>
  );
}

/** Wrapped, so an unauthenticated visitor gets the sign-in form rather than a screen whose every
 * query 401s. */
export function RisksScreen() {
  return (
    <RequireSession>
      <RisksScreenBody />
    </RequireSession>
  );
}
